import { Controller } from 'stimulus'
import L from 'leaflet';
import { statesData } from '../src/interactive_map/states.js';

// plugins
import 'proj4leaflet';
import 'bootstrap/js/dist/tab';

import 'leaflet/dist/leaflet.css';
import 'css/interactive_map.scss';

export default class extends Controller {
  static values = {
    baseUrl: String,
    activeStateNames: Array,
    stateStatusMap: Object,
    activeFillColor: String,
  }

  connect() {
    // EPSG:2163 US National Atlas Equal Area
    let crs = new L.Proj.CRS(
      'EPSG:2163',
      '+proj=laea +lat_0=45 +lon_0=-100 +x_0=0 +y_0=0 +a=6370997 +b=6370997 +units=m +no_defs',
      {
        resolutions: [8000]
      }
    );

    const mapOptions = {
      attributionControl: false,
      zoomControl: false,
      center: [48, -100],
      zoom: 0,
      crs: crs
    };

    this._map = L.map(this.element, mapOptions);

    // hack for displaying map when it is hidden initially
    document
      .querySelector("[data-bs-target='#interactive-map-tab']")
      .addEventListener('shown.bs.tab', () => this.redrawMap());

    this._map.dragging.disable();
    this._map.touchZoom.disable();
    this._map.doubleClickZoom.disable();
    this._map.scrollWheelZoom.disable();
    this._map.keyboard.disable();

    this._geojson = L.geoJson(statesData, {
      style: (feature) => this.baseStyle(feature),
      onEachFeature: (feature, layer) => {
        if (this.shouldShowTooltip(feature)) {
          layer.bindTooltip(this.featureStatus(feature), {
            sticky: true,
            direction: 'top',
            className: `state-status-tooltip ${this.tooltipClass(feature)}`
          });
        }

        layer.on({
          add: () => {
            L.marker(feature.properties.labelPlace || layer.getCenter(), {
              icon: L.divIcon({
                className: 'text-labels',   // Set class for CSS styling
                html: feature.properties.abbr || ''
              }),
              interactive: false
            }).addTo(this._map);
          },
          mouseover: (e) => this.highlightFeature(e),
          mouseout: (e) => this.geojson.resetStyle(e.target)
        })

        if (!this.isNavigableFeature(feature)) return;

        layer.on({
          click: () => {
            window.open((this.baseUrlValue + '/' + feature.properties.name.toLowerCase().replace(/\s/g, '-')), "_blank");
          }
        });
      }
    }).addTo(this.map)
  }

  get map() {
    return this._map;
  }

  get geojson() {
    return this._geojson;
  }

  redrawMap() {
    this._map.invalidateSize();
  }

  hasFilteredStates() {
    return this.hasStateStatusMapValue || this.hasActiveStateNamesValue;
  }

  shouldShowTooltip(feature) {
    if (!this.hasFilteredStates()) return false;
    if (!this.hasStateStatusMapValue) return true;

    return this.isPublishedFeature(feature);
  }

  isPublishedFeature(feature) {
    if (!this.hasStateStatusMapValue) return true;

    return Object.prototype.hasOwnProperty.call(this.stateStatusMapValue, feature.properties.name);
  }

  stateStatus(feature) {
    if (!this.hasStateStatusMapValue) return null;

    return this.stateStatusMapValue[feature.properties.name] || null;
  }

  isNavigableFeature(feature) {
    if (!this.hasFilteredStates()) return true;
    if (!this.hasStateStatusMapValue) return this.isActiveFeature(feature);

    return this.isPublishedFeature(feature);
  }

  isActiveFeature(feature) {
    if (this.hasStateStatusMapValue) {
      const status = this.stateStatus(feature);

      return Boolean(status && status.active);
    }

    if (!this.hasFilteredStates()) return true;

    return this.activeStateNamesValue.includes(feature.properties.name);
  }

  isWarningFeature(feature) {
    const statusCode = this.stateStatus(feature)?.status;

    return ['review', 'pending', 'not_active_yet'].includes(statusCode);
  }

  featureFillColor(feature) {
    if (this.isActiveFeature(feature)) return this.activeFillColor();
    if (this.isWarningFeature(feature)) return '#fef3c7';

    return '#F3F3F3';
  }

  featureBorderColor(feature) {
    if (this.isActiveFeature(feature)) return '#dc2626';
    if (this.isWarningFeature(feature)) return '#d97706';

    return '#9ca3af';
  }

  featureOpacity(feature) {
    if (this.isActiveFeature(feature)) return 1;
    if (this.isWarningFeature(feature)) return 0.9;

    return 0.7;
  }

  baseStyle(feature) {
    if (!this.hasFilteredStates()) {
      return {
        color: '#FFF',
        fill: true,
        fillColor: '#DDD',
        fillOpacity: 1,
        weight: 1
      }
    }

    if (this.hasStateStatusMapValue && !this.isPublishedFeature(feature)) {
      return {
        color: '#FFF',
        fill: true,
        fillColor: '#FAFAFA',
        fillOpacity: 1,
        opacity: 0.45,
        weight: 1
      }
    }

    return {
      color: '#FFF',
      fill: true,
      fillColor: this.featureFillColor(feature),
      fillOpacity: 1,
      opacity: this.featureOpacity(feature),
      weight: 1
    }
  }

  featureStatus(feature) {
    const status = this.stateStatus(feature);

    if (!status) {
      return this.isActiveFeature(feature) ? 'Active' : 'Unknown';
    }

    const meta = status.last_checked_display
      ? `Last checked: ${status.last_checked_display}`
      : status.tooltip_note;

    return `
      <span class="tooltip-label">${status.label}</span>
      <span class="tooltip-meta">${meta || ''}</span>
    `;
  }

  tooltipClass(feature) {
    const status = this.stateStatus(feature);

    if (status && status.status) return status.status;

    return this.isActiveFeature(feature) ? 'active' : 'unknown';
  }

  highlightFeature(e) {
    const layer = e.target;

    if (!this.hasFilteredStates()) {
      layer.setStyle({
        weight: 1,
        color: '#FFF',
        fill: true,
        fillColor: '#3CA8E4',
        fillOpacity: 1
      });

      if (!L.Browser.ie && !L.Browser.opera && !L.Browser.edge) {
        layer.bringToFront();
      }

      return;
    }

    if (this.hasStateStatusMapValue && !this.isPublishedFeature(layer.feature)) {
      layer.setStyle(this.baseStyle(layer.feature));
      return;
    }

    layer.setStyle({
      weight: 2,
      color: this.featureBorderColor(layer.feature),
      fill: true,
      fillColor: this.featureFillColor(layer.feature),
      fillOpacity: 1
    });

    if (!L.Browser.ie && !L.Browser.opera && !L.Browser.edge) {
      layer.bringToFront();
    }
  }

  activeFillColor() {
    return this.hasActiveFillColorValue ? this.activeFillColorValue : '#fef2f2';
  }
}
